import type { SymbolsWidgetModel } from '.';
import { makeHeaderElements } from '../WidgetModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { SymbolsWidgetState } from './SymbolsSelfOwnedModel';
import { SymbolsSelfOwnedModel, symbolsWidgetStateSchema } from './SymbolsSelfOwnedModel';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND } from 'src/features/aggregated-risk/config';
import type { AggregatedRiskWidgetConfig } from 'src/features/aggregated-risk/config';
import { inject, injectable } from 'src/features/ioc';
import type { AggregatedRiskWidgetRuntimeState } from 'src/models/AggregatedRiskWidgetModel';
import { AggregatedRiskWidgetTableModel } from 'src/models/AggregatedRiskWidgetModel/AggregatedRiskWidgetTableModel';
import type { TableModel } from 'src/models/TableModel';
import { getAggregatedRiskTableQueryOptions } from 'src/queries/aggregatedRiskTables';
import type { ReactContainer } from 'src/ReactContainer';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

export function createSymbolsWidgetHeaderElements(config: AggregatedRiskWidgetConfig): WidgetHeaderElement[] {
    return makeHeaderElements<SymbolsWidgetModel['eventBus']>([
        ...COMMON_HEADER_ELEMENTS,
        { type: 'symbolPicker', position: 30, priority: 70, placeholder: 'symbol' },
        { type: 'stickyFilter', position: 40, priority: 80 },
        {
            type: 'columnTemplateMenu',
            position: 70,
            priority: 20,
            columnTemplateKey: config.templateKey,
            alwaysUseGridSettings: true,
        },
        { type: 'tableRowCount', position: 75, priority: 11 },
        { type: 'connectionStatus', position: 76, priority: 12, overflowPosition: 5 },
    ]);
}

@injectable()
export class SymbolsWidgetModelImpl implements SymbolsWidgetModel {
    static readonly stateSchema: z.ZodType<SymbolsWidgetState> = symbolsWidgetStateSchema;

    static defaultState(): SymbolsWidgetState {
        return { columnTemplate: null, filter: null, instrument: null };
    }

    static async fromJSON(container: ReactContainer): Promise<SymbolsWidgetModelImpl> {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        const raw = await getInitialData();
        const validated = SymbolsWidgetModelImpl.stateSchema.parse(raw);
        container.bind('InitialWidgetState').toConstantValue(validated);
        return container.get('SymbolsWidgetModel') as SymbolsWidgetModelImpl;
    }

    readonly #model: SymbolsSelfOwnedModel;
    readonly #table: TableModel;
    readonly #tableModel: AggregatedRiskWidgetTableModel;
    readonly #disposableStack = new DisposableStack();
    readonly headerElements: WidgetHeaderElement[];

    constructor(
        @inject('WidgetId') tabId: WidgetId,
        @inject('WidgetHostContainer') container: WidgetHostContainer,
        @inject('InitialWidgetState') initialState: unknown,
        @inject('Logger') logger: Logger,
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('TableModel') table: TableModel,
    ) {
        const persisted = SymbolsWidgetModelImpl.stateSchema.parse(initialState);
        const config = AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND.symbols;

        this.#table = this.#disposableStack.use(table);

        this.#model = this.#disposableStack.use(
            new SymbolsSelfOwnedModel({
                container,
                tabId,
                persisted,
                logger,
                queryClient,
                slugMapServiceProvider,
                table: this.#table,
            }),
        );

        this.#tableModel = this.#disposableStack.use(
            new AggregatedRiskWidgetTableModel({
                columnTemplate: this.#model.columnTemplate,
                columnTemplateError: this.#model.columnTemplateError,
                config,
                devtoolsName: 'Symbols widget',
                groupByIndicatorIds: this.#model.groupByIndicatorIds,
                filters: this.#model.tableFilters,
                slugMapServiceProvider,
                table: this.#table,
                tableKind: 'symbols',
                tableQueryOptionsFactory: getAggregatedRiskTableQueryOptions,
                widgetId: tabId,
            }),
        );

        this.headerElements = createSymbolsWidgetHeaderElements(config);
    }

    get type(): 'symbols' {
        return 'symbols';
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model.dirty;
    }

    get state(): ReadonlySignal<AggregatedRiskWidgetRuntimeState> {
        return this.#tableModel.state;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate | null> {
        return this.#model.columnTemplate;
    }

    get groupByIndicatorIds(): ReadonlySignal<readonly string[]> {
        return this.#model.groupByIndicatorIds;
    }

    get columnTemplateError(): ReadonlySignal<Error | null> {
        return this.#model.columnTemplateError;
    }

    get tableFilters(): SymbolsWidgetModel['tableFilters'] {
        return this.#model.tableFilters;
    }

    get instrument(): ReadonlySignal<string | null> {
        return this.#model.instrument;
    }

    get eventBus(): SymbolsWidgetModel['eventBus'] {
        return this.#model.eventBus;
    }

    get table(): TableModel {
        return this.#table;
    }

    toJSON(): SymbolsWidgetState {
        return this.#model.toJSON();
    }

    markClean(): void {
        this.#model.markClean();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
